import { useContext, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Radio,
  IconButton,
  Divider,
  Alert,
} from '@mui/material'
import { Delete } from '@mui/icons-material'
import { SessionContext } from '../../context/SessionContext'
import { updateSessionFields } from '../../service/sessionService'
import { generatePopQuizQuestions } from '../../tool/getQuiz'
import { showToast } from '../commonUnit/Toast'

const emptyQuestion = () => ({ text: '', choices: ['', '', '', ''], correctIndex: 0 })

// Question authoring for a Pop Quiz session, moved here from AddSessionDialog - a Pop
// Quiz session is now created with just a title and no questions, and the instructor
// adds/edits questions on the session's own page instead, the same way an instructor
// edits a regular exercise's task/tests after creating it rather than only at creation
// time.
const PopQuizEditor = () => {
  const { session, setSession } = useContext(SessionContext)

  const [title, setTitle] = useState(session?.task || '')
  const [questions, setQuestions] = useState(
    session?.quiz?.questions?.length ? session.quiz.questions : [emptyQuestion()]
  )
  const [quizTopic, setQuizTopic] = useState('')
  const [numAiQuestions, setNumAiQuestions] = useState(5)
  const [isGeneratingQuiz, setIsGeneratingQuiz] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [formError, setFormError] = useState('')

  // Sync local editing state if a different session loads into context (e.g. navigating
  // from one Pop Quiz session's page to another without a full page reload).
  useEffect(() => {
    setTitle(session?.task || '')
    setQuestions(session?.quiz?.questions?.length ? session.quiz.questions : [emptyQuestion()])
  }, [session?.id])

  const updateQuestion = (index, patch) => {
    setQuestions(current => current.map((q, i) => i === index ? { ...q, ...patch } : q))
    setFormError('')
  }

  const updateChoice = (qIndex, cIndex, value) => {
    setQuestions(current => current.map((q, i) => {
      if (i !== qIndex) return q
      const choices = [...q.choices]
      choices[cIndex] = value
      return { ...q, choices }
    }))
    setFormError('')
  }

  const addQuestion = () => setQuestions(current => [...current, emptyQuestion()])
  const removeQuestion = (index) => setQuestions(current => current.filter((_, i) => i !== index))

  const handleGenerateQuizWithAI = async () => {
    if (!quizTopic.trim()) {
      setFormError('Enter a topic or instructions for the AI to generate questions from.')
      return
    }
    setIsGeneratingQuiz(true)
    setFormError('')
    try {
      const result = await generatePopQuizQuestions(quizTopic, numAiQuestions)
      const generated = (result?.questions || []).map(q => ({
        text: q.text || '',
        choices: Array.isArray(q.choices) && q.choices.length === 4 ? q.choices : ['', '', '', ''],
        correctIndex: typeof q.correctIndex === 'number' ? q.correctIndex : 0,
      }))
      if (generated.length === 0) {
        setFormError('The AI did not return any questions. Try a more specific topic.')
        return
      }
      setQuestions(current => {
        const isBlankStarter = current.length === 1 && !current[0].text && current[0].choices.every(c => !c)
        return isBlankStarter ? generated : [...current, ...generated]
      })
    } catch (error) {
      setFormError('VizPI could not generate quiz questions. Check the backend console and try again.')
    } finally {
      setIsGeneratingQuiz(false)
    }
  }

  const handleSave = async () => {
    if (!title.trim()) {
      setFormError('Enter a quiz title.')
      return
    }
    const cleaned = questions
      .map(q => ({ ...q, text: q.text.trim(), choices: q.choices.map(c => c.trim()) }))
      .filter(q => q.text || q.choices.some(c => c))
    const incomplete = cleaned.find(q => !q.text || q.choices.some(c => !c))
    if (incomplete) {
      setFormError('Every question needs text and all 4 answer choices filled in.')
      return
    }

    setIsSaving(true)
    setFormError('')
    const fields = { task: title.trim(), quiz: { questions: cleaned } }
    const ok = await updateSessionFields(session.id, fields)
    setIsSaving(false)
    if (ok) {
      setSession(current => ({ ...current, ...fields }))
      showToast('Quiz saved!', 'success', 2000)
    } else {
      setFormError('VizPI could not save the quiz. Check the backend console and try again.')
    }
  }

  if (!session) return null

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>Pop Quiz — {session.subject}</Typography>
      {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

      <TextField
        label="Quiz Title"
        fullWidth
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        sx={{ mb: 3 }}
      />

      <Divider sx={{ mb: 2 }} />

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Generate questions with AI</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'flex-start' }}>
        <TextField
          size="small"
          label="Topic / instructions"
          value={quizTopic}
          onChange={(e) => setQuizTopic(e.target.value)}
          fullWidth
        />
        <TextField
          size="small"
          label="# questions"
          type="number"
          inputProps={{ min: 1, max: 15 }}
          value={numAiQuestions}
          onChange={(e) => setNumAiQuestions(Math.max(1, Math.min(15, Number(e.target.value) || 1)))}
          sx={{ width: 110 }}
        />
        <Button
          variant="outlined"
          onClick={handleGenerateQuizWithAI}
          disabled={isGeneratingQuiz}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {isGeneratingQuiz ? <CircularProgress size={20} /> : 'Generate'}
        </Button>
      </Box>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Questions ({questions.length}) - click the circle to mark the correct answer
      </Typography>
      {questions.map((q, qIndex) => (
        <Box key={qIndex} sx={{ mb: 2, p: 1.5, border: '1px solid #e0e0e0', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small"
              label={`Question ${qIndex + 1}`}
              value={q.text}
              onChange={(e) => updateQuestion(qIndex, { text: e.target.value })}
              fullWidth
            />
            <IconButton
              size="small"
              onClick={() => removeQuestion(qIndex)}
              disabled={questions.length === 1}
              title="Remove question"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Box>
          {q.choices.map((choice, cIndex) => (
            <Box key={cIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Radio
                size="small"
                checked={q.correctIndex === cIndex}
                onChange={() => updateQuestion(qIndex, { correctIndex: cIndex })}
              />
              <TextField
                size="small"
                label={`Choice ${cIndex + 1}`}
                value={choice}
                onChange={(e) => updateChoice(qIndex, cIndex, e.target.value)}
                fullWidth
              />
            </Box>
          ))}
        </Box>
      ))}
      <Button size="small" onClick={addQuestion} sx={{ mb: 3 }}>+ Add Question</Button>

      <Box>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? <CircularProgress size={20} /> : 'Save Quiz'}
        </Button>
      </Box>
    </Box>
  )
}

export default PopQuizEditor
